import {
  Alert,
  Anchor,
  Badge,
  Blockquote,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconSparkles } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Fragment as ReactFragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { api } from "../../api/client";
import type { SearchQuery } from "../../api/hooks";
import type { Summary } from "../../api/types";
import { formatDate } from "../../lib/format";

/**
 * "Answer with sources": the local model picks the sentences of the current search fragments that
 * answer the question, a separate check confirms them, and the Armenian answer is written from
 * those only; every sentence carries a [N] marker that links to the source document, and the
 * confirmed sentences are quoted below it. The answer is produced in the background (a CPU-only
 * model needs several minutes) and polled.
 */
export function SummaryPanel({ query }: { query: SearchQuery }) {
  const { t } = useTranslation();
  const [id, setId] = useState<number | null>(null);
  const start = useMutation({
    mutationFn: () =>
      api<{ id: number; status: string; cached: boolean }>("/api/summaries", {
        method: "POST",
        body: {
          q: query.q,
          company: query.company,
          type: query.type,
          from: query.from ?? undefined,
          to: query.to ?? undefined,
        },
      }),
    onSuccess: (r) => setId(r.id),
    onError: (e: Error) => notifications.show({ message: e.message, color: "red" }),
  });
  const summary = useQuery({
    queryKey: ["summary", id],
    enabled: id !== null,
    queryFn: ({ signal }) => api<Summary>(`/api/summaries/${id}`, { signal }),
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === "queued" || s === "running" ? 2000 : false;
    },
  });
  const s = summary.data;
  const sourceById = new Map((s?.sources ?? []).map((src) => [src.n, src]));
  // The sentences the answer rests on, shown verbatim (answers made before the evidence gate
  // have none).
  const evidence = (s?.evidence ?? []).filter((e) => e.verified);

  // [N] and [N, M] markers become links to the cited documents.
  const renderLine = (line: string) => {
    const parts = line.split(/(\[\d+(?:\s*[,;]\s*\d+)*\])/g);
    return parts.map((part, i) => {
      const m = /^\[(\d+(?:\s*[,;]\s*\d+)*)\]$/.exec(part);
      if (!m) return <ReactFragment key={i}>{part}</ReactFragment>;
      const ns = (m[1] as string).split(/[,;]/).map((x) => Number(x.trim()));
      return (
        <ReactFragment key={i}>
          [
          {ns.map((n, j) => {
            const src = sourceById.get(n);
            return (
              <ReactFragment key={n}>
                {j > 0 ? ", " : ""}
                {src ? (
                  <Anchor
                    component={Link}
                    to={`/documents/${src.documentId}?q=${encodeURIComponent(query.q)}&page=${src.pageNo}`}
                    fw={600}
                  >
                    {n}
                  </Anchor>
                ) : (
                  n
                )}
              </ReactFragment>
            );
          })}
          ]
        </ReactFragment>
      );
    });
  };

  return (
    <Paper withBorder p="md" bg="var(--mantine-color-default-hover)">
      <Stack gap="xs">
        <Group justify="space-between" wrap="wrap">
          <Group gap="xs">
            <IconSparkles size={18} />
            <Text fw={600}>{t("summary.title")}</Text>
            {s?.status === "done" && s.tookMs !== null && (
              <Text size="xs" c="dimmed">
                {t("summary.took", { s: Math.round(s.tookMs / 1000) })} · {s.model}
              </Text>
            )}
          </Group>
          {!id && (
            <Button
              size="xs"
              variant="light"
              leftSection={<IconSparkles size={14} />}
              loading={start.isPending}
              onClick={() => start.mutate()}
            >
              {t("summary.button")}
            </Button>
          )}
        </Group>
        {!id && (
          <Text size="xs" c="dimmed">
            {t("summary.hint")}
          </Text>
        )}
        {s && (s.status === "queued" || s.status === "running") && (
          <Group gap="xs">
            <Loader size="xs" />
            <Text size="sm" c="dimmed">
              {s.status === "queued"
                ? t("summary.queued")
                : s.phase
                  ? t(`summary.phase.${s.phase}`)
                  : t("summary.running")}
            </Text>
          </Group>
        )}
        {s && s.status === "running" && s.text && (
          <Text size="sm" style={{ whiteSpace: "pre-wrap", opacity: 0.8 }}>
            {s.text}
          </Text>
        )}
        {s?.status === "failed" && (
          <Alert color="red" title={t("summary.failed")}>
            {s.error}
          </Alert>
        )}
        {s?.status === "done" && (
          <Stack gap={4}>
            {s.text.split("\n").map((line, i) => (
              <Text key={i} size="sm" fw={/[:՝]$/.test(line) ? 600 : 400}>
                {renderLine(line)}
              </Text>
            ))}
            {evidence.length > 0 && (
              <Stack gap={6} mt={6}>
                <Text size="sm" fw={600}>
                  {t("summary.evidence")}
                </Text>
                {evidence.map((e) => (
                  <Blockquote key={e.label} p="xs" pl="sm" color="gray" radius="sm">
                    <Text size="sm">«{e.text}»</Text>
                    {/* The same sentence may stand in several acts (versions, boilerplate). */}
                    {[e.n, ...e.also].map((n) => {
                      const src = sourceById.get(n);
                      if (!src) return null;
                      return (
                        <Anchor
                          key={n}
                          component={Link}
                          to={`/documents/${src.documentId}?q=${encodeURIComponent(query.q)}&page=${src.pageNo}`}
                          size="xs"
                          c="dimmed"
                          display="block"
                        >
                          [{n}]{" "}
                          {[
                            src.title,
                            src.docType,
                            src.regNumber ? `№ ${src.regNumber}` : null,
                            src.docDate ? formatDate(src.docDate) : null,
                            t("search.page", { n: src.pageNo }),
                            src.clause ? t("search.clause", { n: src.clause }) : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </Anchor>
                      );
                    })}
                  </Blockquote>
                ))}
              </Stack>
            )}
            {(s.dropped > 0 || s.unsupported > 0) && (
              <Text size="xs" c="dimmed">
                {t("summary.filtered", { dropped: s.dropped, unsupported: s.unsupported })}
              </Text>
            )}
            <Group gap={6} wrap="wrap" mt={4}>
              {s.sources.map((src) => (
                <Badge
                  key={src.n}
                  variant="outline"
                  color="gray"
                  component={Link}
                  to={`/documents/${src.documentId}?q=${encodeURIComponent(query.q)}&page=${src.pageNo}`}
                  style={{ cursor: "pointer", textTransform: "none", maxWidth: 320 }}
                  title={src.title}
                >
                  [{src.n}] {src.regNumber ?? src.title.slice(0, 40)} ·{" "}
                  {t("search.page", { n: src.pageNo })}
                  {src.clause ? ` · ${t("search.clause", { n: src.clause })}` : ""}
                </Badge>
              ))}
            </Group>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
